import { useApolloClient } from '@apollo/client'
import { useDispatcher } from '@jusbrasil-web/shared-utils-events'
import { useEffect, useId, useMemo, useState } from 'react'
import { createRoot, Root } from 'react-dom/client'

import {
  useValidateOabModalContextEmpty,
  ValidateOabModalContextEmpty,
  ValidateOabModalContextInitialized,
} from './context'
import { ValidateOabModal, ValidateOabModalProps } from './validate-oab-modal'

type UseValidateOabModalProps = Omit<ValidateOabModalProps, 'client' | 'setContext' | 'sendMetrics'>

export function useValidateOabModal<T extends HTMLElement>(props: UseValidateOabModalProps = {}) {
  const id = useId()
  const [context, setContext] = useState<
    ValidateOabModalContextEmpty | ValidateOabModalContextInitialized
  >(useValidateOabModalContextEmpty())
  const { open, setOpen } = context
  const client = useApolloClient()
  const { sendMetrics, ref } = useDispatcher<T>()
  const [root, setRoot] = useState<Root | null>(null)

  useEffect(() => {
    const div = document.createElement('div')
    document.body.appendChild(div)
    const rootComponent = createRoot(div, { identifierPrefix: id })
    setRoot(rootComponent)

    return () => {
      setRoot(null)

      // to avoid React warnings about unmounting a component during a state update
      setTimeout(() => {
        rootComponent.unmount()
        document.body.removeChild(div)
      })
    }
  }, [id])

  const [validateOabModalProps, setValidateOabModalProps] =
    useState<UseValidateOabModalProps>(props)
  useEffect(() => {
    if (JSON.stringify(validateOabModalProps) !== JSON.stringify(props)) {
      setValidateOabModalProps(props)
    }
  }, [validateOabModalProps, props])

  useEffect(() => {
    if (root) {
      root.render(
        <ValidateOabModal
          {...validateOabModalProps}
          client={client}
          setContext={setContext}
          sendMetrics={sendMetrics}
        />
      )
    }
  }, [client, root, sendMetrics, validateOabModalProps])

  return useMemo(
    () => ({
      open,
      setOpen,
      metricsRef: ref,
    }),
    [open, setOpen, ref]
  )
}
